// DefectDatabaseSearch.js - Smart search component for building defects database

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { 
  Box, 
  TextField,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  Button,
  Collapse,
  LinearProgress
} from '@mui/material';
import { 
  Search,
  AutoAwesome,
  Close as X,
  Category,
  LocationOn,
  PriorityHigh
} from '@mui/icons-material';
import { 
  searchDefectsDatabase as searchAPI, 
  getDefectSuggestionsByElement as getSuggestionsAPI
} from '../../../../../api/defectDatabaseApi';
import { applyDefectDatabaseToForm } from '../defectHelper';

const DefectDatabaseSearch = ({ 
  setFieldValue, 
  elementName, 
  disabled = false,
  onDefectSelected 
}) => {
  const scope = useSelector((state) => state.container.scope);
  const system = useSelector((state) => state.system);
  
  // Get current system language (en/de) for UI and fallbacks
  const systemLanguage = system?.language || scope?.projectLanguageCode || 'de';
  
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [elementSuggestions, setElementSuggestions] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Language detection with comprehensive German/English technical terms
  const detectedLanguage = useMemo(() => {
    // If no search term, use system language for element-based suggestions
    if (!searchTerm) return systemLanguage;
    
    // English technical terms commonly used in building defects
    const englishTerms = [
      'ventilation', 'heating', 'electrical', 'plumbing', 'roof', 'basement',
      'defect', 'repair', 'maintenance', 'damage', 'leak', 'corrosion',
      'window', 'door', 'wall', 'ceiling', 'floor', 'insulation',
      'moisture', 'elevator', 'cabin', 'drive', 'distribution', 'cables',
      'pipes', 'radiators', 'boiler', 'thermostat', 'switches', 'sockets',
      'facade', 'thermal', 'structural', 'mechanical'
    ];
    
    // German technical terms commonly used in building defects  
    const germanTerms = [
      'heizung', 'lüftung', 'elektro', 'sanitär', 'dach', 'keller',
      'defekt', 'reparatur', 'wartung', 'schaden', 'leckage', 'korrosion',
      'fenster', 'tür', 'wand', 'decke', 'boden', 'dämmung',
      'feuchtigkeit', 'aufzug', 'kabine', 'antrieb', 'verteilung', 'kabel',
      'rohre', 'heizkörper', 'kessel', 'thermostat', 'schalter', 'steckdosen',
      'fassade', 'wärme', 'struktur', 'mechanisch', 'mangel', 'instandsetzung'
    ];
    
    const searchLower = searchTerm.toLowerCase();
    const hasEnglishTerms = englishTerms.some(term => searchLower.includes(term));
    const hasGermanTerms = germanTerms.some(term => searchLower.includes(term));
    const hasGermanChars = /[äöüßÄÖÜ]/.test(searchTerm);
    
    // Determine language with priority: German chars > specific terms > system language
    if (hasGermanChars || hasGermanTerms) {
      return 'de';
    } else if (hasEnglishTerms) {
      return 'en';
    } else {
      return systemLanguage;
    }
  }, [searchTerm, systemLanguage]);
  
  // Search functionality using API
  const handleSearch = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      setIsSearchOpen(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const results = await searchAPI(term, { language: detectedLanguage });
      setSearchResults(results);
      setIsSearchOpen(results.length > 0);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search defects database');
      setSearchResults([]);
      setIsSearchOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, [detectedLanguage]);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        handleSearch(searchTerm);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, handleSearch]);
  
  // Get element-based suggestions
  useEffect(() => {
    const loadSuggestions = async () => {
      if (elementName) {
        setIsLoading(true);
        
        try {
          const suggestions = await getSuggestionsAPI(elementName, detectedLanguage);
          setElementSuggestions(suggestions);
        } catch (error) {
          console.error('Error loading suggestions:', error);
          setElementSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setElementSuggestions([]);
      }
    };

    loadSuggestions();
  }, [elementName, detectedLanguage]);
  
  // Handle defect selection
  const handleDefectSelect = useCallback((defect) => {
    // Apply defect data to form using detected language
    applyDefectDatabaseToForm(defect, setFieldValue, detectedLanguage, false);
    
    setIsSearchOpen(false);
    setSearchResults([]);
    setSearchTerm('');
    if (onDefectSelected) {
      onDefectSelected(defect);
    }
  }, [setFieldValue, onDefectSelected, detectedLanguage, searchTerm, systemLanguage]);
  
  // Render defect card
  const renderDefectCard = (defect, index) => {
    const lang = detectedLanguage;
    const priorityLabels = {
      de: { kurzfristig: 'Kurzfristig', mittelfristig: 'Mittelfristig', langfristig: 'Langfristig' },
      en: { kurzfristig: 'Short-term', mittelfristig: 'Medium-term', langfristig: 'Long-term' }
    };
    
    return (
      <Card 
        key={defect.id} 
        sx={{ 
          mb: 1, 
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': { 
            boxShadow: 3,
            transform: 'translateY(-1px)'
          }
        }}
        onClick={() => handleDefectSelect(defect)}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack spacing={1}>
            {/* Header with element name and priority */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1, mr: 1 }}>
                {defect.element?.[lang] || defect.element?.de || defect.element?.en || 'Unknown Element'}
              </Typography>
              <Chip 
                label={priorityLabels[lang]?.[defect.priority] || defect.priority || 'Unknown'} 
                size="small" 
                color={defect.severity === 'high' ? 'error' : defect.severity === 'medium' ? 'warning' : 'success'}
                sx={{ fontSize: '0.7rem' }}
              />
            </Box>
            
            {/* Defect type and category */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={defect.defectType?.[lang] || defect.defectType?.de || defect.defectType?.en || 'Unknown Type'}
                size="small"
                variant="outlined"
                icon={<Category sx={{ fontSize: 14 }} />}
                sx={{ fontSize: '0.7rem' }}
              />
              <Chip 
                label={(defect.buildingPart || 'unknown').replace(/_/g, ' ')}
                size="small"
                variant="outlined"
                icon={<LocationOn sx={{ fontSize: 14 }} />}
                sx={{ fontSize: '0.7rem' }}
              />
            </Box>
            
            {/* Description preview */}
            <Typography variant="body2" color="text.secondary" sx={{ 
              fontSize: '0.8rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}>
              {defect.description?.[lang] || defect.description?.de || defect.description?.en || 'No description available'}
            </Typography>
            
            {/* Cost information with Min, Average, Max */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" color="primary.main" fontWeight="bold">
                  Average: €{defect.cost?.basePrice || 0}/{(defect.cost?.unit || '€/m²').replace('€/', '')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Min: €{defect.cost?.minPrice || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Max: €{defect.cost?.maxPrice || 0}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };
  
  if (disabled) return null;
  
  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      {/* Compact Search Input */}
      <TextField
        fullWidth
        size="small"
        placeholder={systemLanguage === 'de' ? 
          "Suche in der Mangeldatenbank (z.B. 'Keller Feucht', 'Dach Lecka')..." :
          "Search defects database (e.g., 'Basement Moisture', 'Roof Leaks')..."
        }
        value={searchTerm}
        onChange={(e) => {
          const value = e.target.value;
          setSearchTerm(value);
          // handleSearch now happens via debounced useEffect
          
          // Close element suggestions when user starts typing search
          if (value.length > 0) {
            setIsSearchOpen(false);
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: (searchTerm || searchResults.length > 0) && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => {
                setSearchTerm('');
                setSearchResults([]);
                setIsSearchOpen(false);
              }}>
                <X sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          )
        }}
        sx={{
          mb: 1,
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#fafafa',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
            '&.Mui-focused': {
              backgroundColor: 'white',
            },
          },
        }}
      />

      {/* Loading indicator */}
      {isLoading && (
        <LinearProgress sx={{ mb: 1, height: 2 }} />
      )}

      {/* Element suggestions with detailed view - only show when no active search */}
      {elementSuggestions.length > 0 && !searchTerm && searchResults.length === 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ mb: 0.5, color: 'text.secondary', display: 'block' }}>
            {systemLanguage === 'de' ? 
              `Empfohlene Mängel für "${elementName}":` :
              `Suggested defects for "${elementName}":`
            }
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            startIcon={<AutoAwesome />}
            sx={{ mb: 1, fontSize: '0.8rem' }}
          >
            {systemLanguage === 'de' ? 
              `${elementSuggestions.length} Vorschläge anzeigen` :
              `Show ${elementSuggestions.length} suggestions`
            }
          </Button>
          
          <Collapse in={isSearchOpen}>
            <Box sx={{ maxHeight: 300, overflow: 'auto', p: 1, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.300' }}>
              {elementSuggestions.map((defect, index) => (
                <div key={`element-suggestion-${defect.id}-${index}`}>
                  {renderDefectCard(defect, index)}
                </div>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Search results with detailed cards */}
      {searchResults.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {systemLanguage === 'de' ? `Suchergebnisse (${searchResults.length})` : `Search Results (${searchResults.length})`}
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => {
                setSearchResults([]);
                setIsSearchOpen(false);
                setSearchTerm('');
              }}
            >
              <X sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Box sx={{ maxHeight: 400, overflow: 'auto', p: 1, bgcolor: 'white', border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
            {searchResults.map((defect, index) => (
              <div key={`search-result-${defect.id}-${index}`}>
                {renderDefectCard(defect, index)}
              </div>
            ))}
          </Box>
        </Box>
      )}

      {/* Expanded element suggestions when showing more */}
      {/* Removed duplicate section - element suggestions are handled above in the collapse */}
      
      {/* No results message */}
      {searchTerm.length >= 2 && searchResults.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          {systemLanguage === 'de' ? 
            `Keine Mängel für "${searchTerm}" gefunden. Versuchen Sie andere Suchbegriffe.` :
            `No defects found for "${searchTerm}". Try different keywords.`
          }
        </Typography>
      )}
    </Box>
  );
};

export default DefectDatabaseSearch;
